import React, { useState, useCallback } from "react";
import { useTrapSkuStore } from "../../stores/trapSkuStore";
import { useTrapsSelectionStore } from "../../stores/trapsSelectionStore";
import { noImage, coinIcon } from "assets";
import {
  Icon,
  PermissionWrapper,
  RowActionsMenu,
  useConfirmationStore,
} from "components";
import CategoryCell from "./TrapSkuTableRowComponents/CategoryCell";
import PriceCell from "./TrapSkuTableRowComponents/PriceCell";
import PresenceCell from "./TrapSkuTableRowComponents/PresenceCell";
import {
  PRESENCE_OPTIONS,
  getPresenceLabel,
  getPlatformLabel,
} from "../../utils/trapSkuConfig";

// Компонент відображення рядка таблиці сторінки "Товари ловушок"
const TrapSkuTableRow = ({ item, mappedCategories, onImageClick }) => {
  // Отримуємо дані та дії зі стору ловушок
  const updateTrapSkuPrice = useTrapSkuStore((state) => state.updateTrapSkuPrice);
  const updateTrapCustomPrice = useTrapSkuStore((state) => state.updateTrapCustomPrice);
  const removeCustomPrice = useTrapSkuStore((state) => state.removeCustomPrice);
  const updateTrapSkuCategory = useTrapSkuStore((state) => state.updateTrapSkuCategory);
  const updateTrapSkuPresence = useTrapSkuStore((state) => state.updateTrapSkuPresence);
  const toggleExpand = useTrapSkuStore((state) => state.toggleExpand);
  const expandedTraps = useTrapSkuStore((state) => state.expandedTraps);
  const toggleAuto = useTrapSkuStore((state) => state.toggleAuto);

  // Хук для відображення вікна підтвердження
  const showConfirmation = useConfirmationStore((state) => state.showConfirmation);

  // Отримуємо дані та дії зі стору вибору ловушок
  const isSelectionModeActive = useTrapsSelectionStore(
    (state) => state.isSelectionModeActive,
  );
  const selectedSkus = useTrapsSelectionStore((state) => state.selectedSkus);
  const toggleSku = useTrapsSelectionStore((state) => state.toggleSku);

  const isSelected = selectedSkus.includes(item.sku);
  const isExpanded = !!expandedTraps[item.sku];
  const hasProducts = (item.product_count ?? 0) > 0;

  // Локальний стан для керування редагуванням
  const [editing, setEditing] = useState({ field: null, value: "" });

  // Скасовує режим редагування, очищуючи локальний стан
  const cancelEditing = useCallback(() => {
    setEditing({ field: null, value: "" });
  }, []);

  // Надсилає запит на оновлення даних на сервер
  const handleUpdate = (field, newValue) => {
    // Запобігаємо зайвим запитам, якщо значення не змінилося
    if (String(item[field]) === String(newValue) || String(newValue).trim() === "") {
      cancelEditing();
      return;
    }

    // Викликаємо відповідну функцію оновлення залежно від поля
    switch (field) {
      case "price":
        const numericValue = parseFloat(newValue);
        if (!isNaN(numericValue) && numericValue >= 0) {
          // Кастомну ціну редагуємо як кастомну, звичайну — як оригінальну
          if (item.is_custom_price) {
            updateTrapCustomPrice(item.sku, numericValue);
          } else {
            updateTrapSkuPrice(item.sku, numericValue);
          }
        }
        break;
      case "category_id":
        updateTrapSkuCategory(item.sku, newValue);
        break;
      case "presence":
        updateTrapSkuPresence(item.sku, newValue);
        break;
      default:
        console.warn(`Невідоме поле для оновлення: ${field}`);
    }
    cancelEditing(); // Виходимо з режиму редагування після надсилання запиту
  };

  // Запитує підтвердження та видаляє кастомну ціну товару
  const handleRemoveCustomPrice = () => {
    showConfirmation({
      title: "Видалення кастомної ціни",
      message: `Видалити кастомну ціну для "${item.product_name || item.sku}"? Товар повернеться до базової ціни.`,
      onConfirm: () => removeCustomPrice(item.sku),
    });
  };

  // Обробляє натискання клавіш 'Enter' (зберегти) та 'Escape' (скасувати)
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleUpdate(editing.field, editing.value);
    } else if (e.key === "Escape") {
      cancelEditing();
    }
  };

  return (
    <tr key={item.sku} className={isExpanded ? "expanded-row" : ""}>
      {isSelectionModeActive && (
        <td className="selection">
          <input
            className="focus-checkbox"
            type="checkbox"
            checked={isSelected}
            onChange={() => toggleSku(item.sku)}
            title="Обрати"
          />
        </td>
      )}

      {/* Товарів — кількість із розгортанням */}
      <td
        className={`expand-cell ${hasProducts ? "clickable" : ""}`}
        onClick={hasProducts ? () => toggleExpand(item.sku) : undefined}
        title={hasProducts ? "Показати товари" : ""}
      >
        <div className="expand-wrapper">
          {hasProducts && (
            <span className={`expand-icon ${isExpanded ? "expanded" : ""}`}>▶</span>
          )}
          <span className="count-badge">{item.product_count ?? 0}</span>
        </div>
      </td>

      <td>
        <img
          src={item.product_image || noImage}
          className={`${item.product_image ? "clickable" : ""}`}
          onClick={() => onImageClick(item.product_image)}
          alt={item.product_name || item.sku}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = noImage;
          }}
        />
      </td>

      <td className="name">
        <span>{item.product_name || "N/A"}</span>

        {item.is_custom_price && (
          <span className="custom-price-badge" title="Кастомна ціна">
            <Icon src={coinIcon} />
          </span>
        )}
      </td>

      <td className="shop-cell">
        {item.platform && (
          <span
            className={`platform-badge platform-${item.platform}`}
            title={`Майданчик: ${getPlatformLabel(item.platform)}`}
          >
            {getPlatformLabel(item.platform)}
          </span>
        )}
        <span>{item.shop_name}</span>
      </td>

      <td style={{ whiteSpace: "pre" }}>{item.sku}</td>

      <CategoryCell
        item={item}
        editing={editing}
        setEditing={setEditing}
        handleUpdate={handleUpdate}
        handleKeyDown={handleKeyDown}
        mappedCategories={mappedCategories}
      />

      <PriceCell
        item={item}
        editing={editing}
        setEditing={setEditing}
        handleUpdate={handleUpdate}
        handleKeyDown={handleKeyDown}
      />

      <PresenceCell
        item={item}
        editing={editing}
        setEditing={setEditing}
        handleUpdate={handleUpdate}
        options={PRESENCE_OPTIONS} // Передаємо як пропси
        getLabel={getPresenceLabel} // Передаємо як пропси
      />

      {/* Авто — тільки чекбокс */}
      <PermissionWrapper
        requiredPermission="traps:edit"
        fallback={<td className="auto-cell">{item.auto ? "✓" : "—"}</td>}
      >
        <td className="auto-cell">
          <input
            className="focus-checkbox"
            type="checkbox"
            checked={!!item.auto}
            onChange={() => toggleAuto(item.sku, !item.auto)}
            title={item.auto ? "Вимкнути авто-режим" : "Увімкнути авто-режим"}
          />
        </td>
      </PermissionWrapper>

      <PermissionWrapper requiredPermission="traps:edit" fallback={<td></td>}>
        <td className="action-cell">
          <RowActionsMenu
            actions={[
              {
                label: "Видалити кастомну ціну",
                onClick: handleRemoveCustomPrice,
                disabled: !item.is_custom_price,
                danger: true,
              },
            ]}
          />
        </td>
      </PermissionWrapper>
    </tr>
  );
};

export default TrapSkuTableRow;
